/* eslint-disable no-param-reassign */
import React from "react"
import _ from "lodash"
import { TableRow, TableCell } from "@material-ui/core"
import Cell from "./Cell"
import HeadCell from "./HeadCell"
import { inferTextAlignFromFilterType, LEFT } from "../tableUtil"

// TODO: Handle options and follow data given by Jump
const options = {
  filter: false,
  search: false,
  print: false,
  download: false,
  pagination: false,
  rowsPerPage: 10,
  rowsPerPageOptions: [10, 50, 100],
  isRowSelectable: false,
  disableToolbarSelect: true,
  sort: false,
  viewColumns: false,
  selectableRows: "none",
  customToolbar: null,
  setTableProps: () => ({
    padding: "default",
    size: "small"
  }),
  // eslint-disable-next-line react/display-name
  customRowRender: (data, dataIdx) => (
    <TableRow key={dataIdx}>
      {data.map((cell, index) => {
        if (index === 0) {
          return null
        }
        const cellStyle = cell?.props?.style || {}
        return (
          <TableCell
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            component="td"
            style={{ ...cellStyle }}
          >
            {cell}
          </TableCell>
        )
      })}
    </TableRow>
  )
}

/**
 * It infers cell style alignment of table using first row.
 * @param columns
 * @param controlRow
 * @returns {{}|*}
 */
function inferCellAlignmentByTypeOfRow(columns, controlRow) {
  if (_.isEmpty(controlRow)) {
    return {}
  }

  return columns.reduce((result, col) => {
    const cellObj = controlRow[col.dataIndex]
    result[col.dataIndex] = inferTextAlignFromFilterType(cellObj.filter)
    return result
  }, {})
}

/**
 * An adapter that transform JUMP's table options into Mui-Datatables options.
 */
export default class MuiDatatablesAdapter {
  /**
   *
   * @param data declarative from JUMP's table options
   */
  constructor(data) {
    this.rows = data.data
    this.columns = data.columns
    const tableOptions = JSON.parse(JSON.stringify(options)) // Create deep copy of 'options'
    // You can't deep copy functions, so get those references back
    tableOptions.setTableProps = options.setTableProps
    tableOptions.customRowRender = options.customRowRender
    tableOptions.hideHeaders = data.hideHeaders

    // JUMP can send us back a property called 'rowsPerTablePage' used to enable pagination, we're using it here
    if (data.rowsPerTablePage && data.rowsPerTablePage > 0) {
      const { rowsPerPageOptions } = tableOptions
      tableOptions.pagination = true
      tableOptions.rowsPerPage = data.rowsPerTablePage
      // Update rowsPerPageOptions array to add new value if it doesn't exist
      if (!rowsPerPageOptions.includes(data.rowsPerTablePage)) {
        rowsPerPageOptions.push(data.rowsPerTablePage)
        rowsPerPageOptions.sort((a, b) => a - b)
        tableOptions.rowsPerPageOptions = rowsPerPageOptions
      }
    }

    // this avoids to infer cell style at each time while processing table matrix
    // it improves significantly performance, processing only the first row
    // a disavantage of that approach is that, if cell types are different between rows
    // it does not handle that case
    this.cellAlignement = inferCellAlignmentByTypeOfRow(
      this.columns,
      _.first(this.rows)
    )
    this.options = tableOptions
  }

  mapRows() {
    return _.map(this.rows, (row, idx) => {
      const rowValues = _.mapValues(row, (col) => ({
        text: col.text || "",
        ...col
      }))
      return { _raw: idx, ...rowValues }
    })
  }

  mapColumns(columnsOptions) {
    // First column is raw Id to be able to remap
    const firstHiddenColumn = {
      name: "_raw",
      options: {
        display: "excluded"
      }
    }

    const others = _.map(this.columns, this.configureColumn(columnsOptions))
    return [firstHiddenColumn, ...others]
  }

  /**
   * Configure Mui-Datatables column options
   *
   * TODO Support following properties bound with JUMP:
   *      align: col.align,
   *      filter: filterTypes[col.filter],
   *      filterValue: (cell) => cell.filter,
   *      style: col.style,
   *      sort: true,
   *      --> Must fix jump first as JUMP is sending hidden when it's not
   *
   * @param columnsOptions declarative from JUMP's table options
   * @returns {function(*): {name: *, options: {filter: boolean, customBodyRender: (function(*=, *): *), customHeadRender: HeadCell}, label: *}}
   */
  configureColumn(columnsOptions) {
    const hideHeaders = this.options.hideHeaders ?? false
    const self = this
    return (jumpColDef) => {
      const passedCustomBodyRender = columnsOptions?.customBodyRender
      const passedColumnsOptions = columnsOptions || {}

      // Sample of jumpColDef format:
      // {
      //   align: "right",
      //   columnOccupiedProportion: 0.1384083044982699
      //   columnServerIndex: 0
      //   columnType: ""
      //   dataIndex: "date"
      //   filter: "cell_date"
      //   groupable: true
      //   hidden: false
      //   hideable: true
      //   lockable: true
      //   locked: false
      //   maxColumnSize: 120
      //   multiEdition: false
      //   needDecimalisation: false
      //   shouldNotBeEditable: false
      //   text: "Date"
      //   tree: false
      // }

      // One day if JUMP Report is able to save width
      // const widthPercent = jumpColDef.columnOccupiedProportion * 100
      // const test = widthPercent.toFixed(0)
      // const thStyle = {
      //   width: `${test}%`
      // }

      const colOption = {
        label: jumpColDef.text,
        name: jumpColDef.dataIndex,
        options: {
          ...passedColumnsOptions,
          filter: false,
          customHeadRender: (columnMeta, updateDirection) => (
            <HeadCell
              columnMeta={columnMeta}
              updateDirection={updateDirection}
              jumpColDef={jumpColDef}
            />
          ),
          customBodyRender: (value, tableMeta) => {
            if (passedCustomBodyRender) {
              return passedCustomBodyRender(
                value,
                tableMeta,
                this.rows[tableMeta.rowData[0]][tableMeta.columnData.name]
              )
            }

            if (value == null) {
              return null
            }

            // Get advanced table properties from JUMP Table
            const cell =
              this.rows[tableMeta.rowData[0]][tableMeta.columnData.name]

            // Handle properly undefined style
            const styleOrDefault = { ...cell.style, ...value.style }
            const innerCell =
              this.rows[tableMeta.rowData[0]][tableMeta.columnData.name]
            const textAlign =
              styleOrDefault.textAlign || // Try to get a DEFAULT textAlign from the passed style (comes from JUMP)
              self.cellAlignement[tableMeta.columnData.name] ||
              LEFT
            innerCell.style = { ...styleOrDefault, textAlign }
            return Cell(value, tableMeta, innerCell)
          }
        }
      }

      if (hideHeaders) {
        colOption.options.display = false
      }

      if (jumpColDef.columnType) {
        colOption.type = jumpColDef.columnType
      }
      return colOption
    }
  }
}
